import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, ImagePicker, inputCls } from "./components";

const EMPTY = { name: "", title: "", bio: "", email: "", phone: "", photo: "", is_head: false };

export default function CoachesSection() {
  const { items, loading, reload } = useResource("coaches");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!editing.name.trim() || !editing.title.trim()) {
      toast.error("Please enter the coach's name and title.");
      return;
    }
    setSaving(true);
    const payload = { name: editing.name.trim(), title: editing.title.trim(), bio: editing.bio || "", email: editing.email || "", phone: editing.phone || "", photo: editing.photo || "", is_head: !!editing.is_head };
    try {
      if (editing.id) await api.put(`/admin/coaches/${editing.id}`, payload);
      else await api.post("/admin/coaches", payload);
      toast.success("Coach saved! The Coaches page is updated.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save. Please try again.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/coaches/${deleting.id}`);
      toast.success("Coach removed.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the coach.");
    }
  };

  if (loading) return <Loading />;

  const sorted = [...items].sort((a, b) => (b.is_head ? 1 : 0) - (a.is_head ? 1 : 0));

  return (
    <div data-testid="admin-coaches-section">
      <SectionHeader
        title="Coaches & Staff"
        subtitle="Add or edit coaching staff, upload their photos, and write their bios."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-coach-button">
            <Plus size={17} /> Add New Coach
          </PrimaryBtn>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="coaches-list">
        {sorted.length === 0 && <p className="col-span-full bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">No coaches yet. Click "Add New Coach".</p>}
        {sorted.map((c) => (
          <div key={c.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid={`coach-row-${c.id}`}>
            {c.photo ? (
              <img src={c.photo} alt={c.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-[#0F4C2A]/15" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-[#0F4C2A] flex items-center justify-center shrink-0">
                <span className="font-condensed text-lg font-bold text-[#F59E0B]">{c.name.split(" ").map((n) => n[0]).join("")}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-condensed text-lg font-bold uppercase text-slate-900 truncate flex items-center gap-2">
                {c.name} {c.is_head && <Star size={14} className="text-[#D97706] fill-[#D97706]" />}
              </p>
              <p className="text-xs text-[#D97706] font-bold uppercase tracking-wide">{c.title}</p>
            </div>
            <button onClick={() => setEditing({ ...c })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit coach" data-testid={`coach-edit-${c.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => setDeleting(c)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete coach" data-testid={`coach-delete-${c.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Coach" : "Add New Coach"} onClose={() => setEditing(null)} testid="coach-modal">
          <div className="space-y-4">
            <Field label="Coach Name">
              <input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="coach-form-name" />
            </Field>
            <Field label="Title / Role" hint="e.g. Head Coach, Pitching Coach, JV Head Coach">
              <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="coach-form-title" />
            </Field>
            <Field label="Bio" hint="A short paragraph about their background and experience">
              <textarea rows={4} className={inputCls} value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} data-testid="coach-form-bio" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Email (optional)">
                <input className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="coach-form-email" />
              </Field>
              <Field label="Phone (optional)">
                <input className={inputCls} value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="coach-form-phone" />
              </Field>
            </div>
            <label className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-4 py-3 cursor-pointer">
              <input type="checkbox" checked={!!editing.is_head} onChange={(e) => setEditing({ ...editing, is_head: e.target.checked })} className="h-5 w-5 accent-[#0F4C2A]" data-testid="coach-form-head" />
              <span className="text-sm font-bold text-slate-700">This is the Head Coach (featured at the top of the Coaches page)</span>
            </label>
            <ImagePicker label="Coach Photo" value={editing.photo} onChange={(url) => setEditing({ ...editing, photo: url })} testid="coach-form-photo" />
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="coach-form-save">
                {saving ? "Saving..." : "Save Coach"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="coach-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Remove ${deleting.name} from the coaching staff? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="coach-delete-confirm"
        />
      )}
    </div>
  );
}
